import { useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Typography
} from '@mui/material'
import { useUploadInjectionJobMutation } from 'store/carInjection'
import CONSTANT from 'modules/constant'
import type { ErrorProps } from 'modules/types'

type UploadDialogProps = {
  open: boolean
  onClose: () => void
  onSuccess?: (jobId: string) => void
  onUploadComplete?: () => void
}

const UploadDialog = ({ open, onClose, onSuccess, onUploadComplete }: UploadDialogProps) => {
  const [uploadInjectionJob, uploadResult] = useUploadInjectionJobMutation()
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState<string>('')
  const [snackbar, setSnackbar] = useState({ open: false, message: '', isError: false })

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0]
    setError('')
    if (selected && !selected.name.toLowerCase().endsWith('.csv')) {
      setError('Only .csv files are allowed')
      setFile(null)
      return
    }
    setFile(selected || null)
  }

  const handleUpload = async () => {
    if (!file) return
    const formData = new FormData()
    formData.append('file', file)
    try {
      const result = await uploadInjectionJob(formData).unwrap()
      setSnackbar({ open: true, message: 'File uploaded successfully', isError: false })
      setFile(null)
      onClose()
      if (onSuccess && result.jobId) {
        onSuccess(result.jobId)
      }
      onUploadComplete?.()
    } catch (err) {
      const error = err as ErrorProps
      setSnackbar({
        open: true,
        message: (error.data && error.data.Message) || 'Upload failed',
        isError: true
      })
    }
  }

  const handleClose = () => {
    setFile(null)
    setError('')
    onClose()
  }

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
        <DialogTitle>Upload Injection File</DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ mb: 2 }}>
            Upload a CSV file containing car data to inject.
          </Typography>
          <input
            type='file'
            accept='.csv'
            onChange={handleFileChange}
            style={{ width: '100%' }}
          />
          {error && (
            <Typography color='error' variant='body2' sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant='contained'
            onClick={handleUpload}
            disabled={!file || uploadResult.isLoading}
          >
            {uploadResult.isLoading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={CONSTANT.NOTIFICATION_DURATION}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.isError ? 'error' : 'success'}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  )
}

export default UploadDialog
